/* eslint-disable no-alert */
import { cancelled, select, fork, call, put, take } from 'redux-saga/effects'
import { eventChannel } from 'redux-saga'
import _ from 'lodash'
import firebase from '../config/firebase'
import DriverActions from '../reducers/driverReducer'
import Hasura from '../config/hasura'

const subscribe = ref =>
  eventChannel(emit => {
    const onSnapshot = snapshot => {
      const data = snapshot.val()
      const drivers = {}

      if (_.size(data) > 0) {
        snapshot.forEach(res => {
          drivers[res.key] = res.val()
        })
      }

      emit(drivers)
    }

    ref.on('value', onSnapshot)

    return () => {
      ref.off('value', onSnapshot)
    }
  })

export function* syncDrivers() {
  const dbRef = firebase
    .database()
    .ref('deliverers')
    .orderByChild('family_name')

  const channel = yield call(subscribe, dbRef)

  yield put(DriverActions.setDriversLoading(true))

  try {
    while (true) {
      const drivers = yield take(channel)

      yield put(DriverActions.setDrivers(drivers))
      yield put(DriverActions.setDriversLoading(false))
    }
  } finally {
    if (yield cancelled()) {
      yield put(DriverActions.setDriversLoading(false))
      channel.close()
    }
  }
}

export function* deleteDriver({ driverUID }) {
  const dbRef = firebase.database().ref(`deliverers/${driverUID}`)

  try {
    yield fork([dbRef, dbRef.remove])
  } catch (error) {
    alert(error)
  }
}

export function* saveDriver({ driverUID, data, callBack }) {
  const { region } = yield select(state => state.auth.data)
  const updates = {}
  const dbRef = firebase.database().ref()

  updates[`/deliverers/${driverUID}/family_name`] = data.family_name
  updates[`/deliverers/${driverUID}/first_name`] = data.first_name
  updates[`/deliverers/${driverUID}/status`] = data.status
  updates[`/deliverers/${driverUID}/verified`] = true
  updates[`/deliverers/${driverUID}/agreed_term`] = true
  updates[`/deliverers/${driverUID}/identity_number`] = data.identity_number
  updates[`/deliverers/${driverUID}/package_size`] = data.package_size
  updates[`/deliverers/${driverUID}/delivery_fee_rate`] = Number(data.delivery_fee_rate)
  updates[`/deliverers/${driverUID}/radius`] = data.radius ? Number(data.radius) : null
  updates[`/deliverers/${driverUID}/tel`] = driverUID
  updates[`/deliverers/${driverUID}/aba`] = data.aba
  updates[`/deliverers/${driverUID}/max_payable_amount`] = Number(data.max_payable_amount)
  updates[`/deliverers/${driverUID}/region`] = data.region || region

  try {
    yield put(DriverActions.setDriversAdding(true))
    yield call([dbRef, dbRef.update], updates)
    yield put(DriverActions.setDriver(driverUID, data))
    yield put(DriverActions.setDriversAdding(false))
    yield call(callBack)
  } catch (e) {
    alert(e)
    yield put(DriverActions.setDriversAdding(false))
  }
}

function* getDriverActivities(start_at, end_at) {
  const { drivers } = yield select(state => state.drivers)
  const driver_keys = Object.keys(drivers)
  yield put(DriverActions.setAnalyticLoading(true))

  try {
    const query = `
      query($start_at: numeric!, $end_at: numeric!, $driver_keys: [String!]!){
        muuver_activities(where: {created_at: {_gt: $start_at, _lt: $end_at}, deliverer_key: {_in: $driver_keys}}) {
          firebase_key
          deliverer_key
          onlined_at
          offlined_at
        }
      }
    `

    const variables = { start_at, end_at, driver_keys }
    const { muuver_activities } = yield call(Hasura.execute, query, variables)

    yield put(DriverActions.setAnalyticDriverActivities(muuver_activities))
    yield put(DriverActions.setAnalyticLoading(false))
  } catch (error) {
    alert(error)
    yield put(DriverActions.setAnalyticLoading(false))
  }
}

export function* getAnalyticOrders({ startAt, endAt }) {
  yield put(DriverActions.setAnalyticLoading(true))
  const query = `
    query($start_at: numeric!, $end_at: numeric!){
      orders(where: {created_at: {_gt: $start_at, _lt: $end_at}}) {
        firebase_key
        deliverer_key
        delivery_fee
        created_at
        updated_at
        status
        eta
      }
    }
  `
  const variables = { start_at: startAt, end_at: endAt }

  try {
    const { orders } = yield call(Hasura.execute, query, variables)

    yield put(DriverActions.setAnalyticOrders(orders))
    yield put(DriverActions.setAnalyticLoading(false))
    yield call(getDriverActivities, startAt, endAt)
  } catch (error) {
    alert(error)
    yield put(DriverActions.setAnalyticLoading(false))
  }
}

export function* getDelivererOrders({ startAt, endAt, deliverer }) {
  const performanceRef = firebase
    .database()
    .ref(`deliverer_orders/${deliverer}`)
    .orderByChild('created_at')
    .startAt(parseInt(startAt, 10))
    .endAt(parseInt(endAt, 10))

  const snapshot = yield call([performanceRef, performanceRef.once], 'value')
  const delivererOrders = snapshot.val()
  return delivererOrders
}

export function* toggleDriverOnline({ driver }) {
  const { driverUID: driverUid, online } = driver

  try {
    const callableParams = {
      driverUid,
      online: !online,
    }

    const api = firebase.functions('asia-northeast1').httpsCallable('updateOnlineStatus')

    yield call(api, callableParams)
  } catch (error) {
    alert(error)
  }
}

export function* fetchTimeSlot() {
  const dbRef = firebase.database().ref('opt_configs')

  try {
    const snapShot = yield call([dbRef, dbRef.once], 'value')
    const readyToPickUpFindDriver = snapShot.val().ready_to_pickup_find_driver
    const driverTimeSlot = snapShot.val().ready_to_pickup_timeslot

    yield put(DriverActions.setTimeSlot(driverTimeSlot))
    yield put(DriverActions.setReadyToPickUp(readyToPickUpFindDriver))
    yield put(DriverActions.setReadyToPickUpLoaded(true))
  } catch (err) {
    alert(err)
  }
}

export function* updateTimeSlot({ data }) {
  const dbRef = firebase.database().ref('opt_configs')
  const readyToPickUpFindDriver = data.ready_to_pickup_find_driver
  const driverTimeSlot = data.ready_to_pickup_timeslot

  try {
    yield put(DriverActions.setTimeSlot(driverTimeSlot))
    yield put(DriverActions.setReadyToPickUp(readyToPickUpFindDriver))
    yield call([dbRef, dbRef.update], data)
  } catch (err) {
    alert(err)
  }
}

export function* getNonVerifyTransactions() {
  const collection = firebase
    .firestore()
    .collection('pre_driver_credits')
    .where('status', '==', 'can-not-verify')
    .orderBy('payment_gateway.created_at')

  try {
    const snapshot = yield call([collection, collection.get])
    const transactions = {}

    snapshot.forEach(doc => {
      transactions[doc.id] = { ...doc.data(), id: doc.id }
    })

    yield put(DriverActions.setNonVerifyTransactions(transactions))
  } catch (error) {
    // alert(error)
    console.log(error)
  }
}



// WEBPACK FOOTER //
// ./src/sagas/driverSaga.js